// Lab Workflow billing contract: the per-doctor running ledger, the company bank accounts a clinic
// pays into, and the payment claims a clinic submits with proof.
//
// TWO RULES ENCODED HERE:
//  1. MONEY IS INTEGER PAISA, exactly like the finance module (shared/pricing.ts). No floats reach
//     the DB; the client converts rupees at the edge with `rupeesToPaisa`.
//  2. A BALANCE IS NEVER STORED. It is the running sum of debit − credit over non-void entries, so
//     it cannot drift from the entries a doctor can read on their statement.
import { z } from 'zod';
import { PAYMENT_METHODS, type PaymentMethod } from './finance.js';

export const WF_LEDGER_ENTRY_TYPES = ['charge', 'payment', 'credit_note', 'adjustment'] as const;
export type WfLedgerEntryType = (typeof WF_LEDGER_ENTRY_TYPES)[number];
export const WF_LEDGER_ENTRY_LABELS: Record<WfLedgerEntryType, string> = {
  charge: 'Charge',
  payment: 'Payment',
  credit_note: 'Credit note',
  adjustment: 'Adjustment',
};

/** Which column an entry type lands in. Charges owe; everything else settles. */
export const isDebitType = (t: WfLedgerEntryType): boolean => t === 'charge';

export const WF_PAYMENT_STATUSES = ['pending', 'verified', 'rejected'] as const;
export type WfPaymentStatus = (typeof WF_PAYMENT_STATUSES)[number];
export const WF_PAYMENT_STATUS_LABELS: Record<WfPaymentStatus, string> = {
  pending: 'Awaiting verification',
  verified: 'Verified',
  rejected: 'Rejected',
};

// ---------------------------------------------------------------------------------------------
// Bank accounts
// ---------------------------------------------------------------------------------------------

export interface WfBankAccount {
  id: string;
  label: string;
  bankName: string;
  accountTitle: string;
  accountNumber: string | null;
  iban: string | null;
  branch: string | null;
  instructions: string | null;
  sortOrder: number;
  isActive: boolean;
}

const txt = (max: number) => z.string().trim().max(max).nullish();

export const wfBankAccountSchema = z.object({
  label: z.string().trim().min(1, 'Give the account a label').max(120),
  bankName: z.string().trim().min(1, 'Bank name is required').max(120),
  accountTitle: z.string().trim().min(1, 'Account title is required').max(160),
  accountNumber: txt(60),
  iban: txt(60),
  branch: txt(120),
  instructions: txt(2000),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional(),
});
export type WfBankAccountInput = z.infer<typeof wfBankAccountSchema>;
export const wfBankAccountPatchSchema = wfBankAccountSchema.partial();

// ---------------------------------------------------------------------------------------------
// Ledger
// ---------------------------------------------------------------------------------------------

export interface WfLedgerEntry {
  id: string;
  doctorId: string;
  caseId: string | null;
  caseNumber: string | null;
  paymentId: string | null;
  entryType: WfLedgerEntryType;
  entryDate: string;
  description: string;
  /** Paisa owed by this entry (charges). */
  debit: number;
  /** Paisa settled by this entry (payments, credit notes). */
  credit: number;
  reference: string | null;
  createdByName: string | null;
  createdAt: string;
  isVoid: boolean;
  voidReason: string | null;
  /** Running balance AFTER this entry. Void entries repeat the previous balance. */
  balance: number;
}

/** A doctor group's statement of account — the portal's Ledger screen, in one payload. */
export interface WfStatement {
  doctor: { id: string; code: string; name: string; clinicName: string | null };
  from: string | null;
  to: string | null;
  /** Balance carried in from before `from` (0 when unfiltered). */
  openingBalance: number;
  entries: WfLedgerEntry[];
  totals: {
    charges: number;
    payments: number;
    /** Net of credit notes + adjustments in the window. */
    adjustments: number;
    closingBalance: number;
  };
  /** Submitted but not yet verified — shown as "pending", never netted off the balance. */
  pendingAmount: number;
  pendingCount: number;
}

/** One row of the staff-side "who owes what" rollup. */
export interface WfDoctorBalance {
  doctorId: string;
  code: string;
  name: string;
  clinicName: string | null;
  charged: number;
  paid: number;
  balance: number;
  pendingAmount: number;
  pendingCount: number;
  lastEntryAt: string | null;
}

export interface WfBillingSummary {
  rows: WfDoctorBalance[];
  totals: { charged: number; paid: number; balance: number; pendingAmount: number; pendingCount: number };
}

export const wfChargeSchema = z.object({
  doctorId: z.string().min(1, 'Pick a doctor group'),
  caseId: z.string().nullish(),
  entryType: z.enum(WF_LEDGER_ENTRY_TYPES).default('charge'),
  /** Always positive paisa — the entry type decides whether it debits or credits. */
  amount: z.number().int().positive('Amount must be more than zero').max(1_000_000_000),
  description: z.string().trim().min(2, 'Describe what this is for').max(500),
  entryDate: z.string().min(1).nullish(),
  reference: txt(120),
});
export type WfChargeInput = z.input<typeof wfChargeSchema>;

/** Ported from coo-lab: a money change without a stated reason is not allowed to happen. */
export const wfVoidEntrySchema = z.object({ reason: z.string().trim().min(3, 'A reason is required to void a money entry').max(500) });
/** Change a ledger line's amount (paisa). See server workflow/ledger-edit.ts for how each kind is corrected. */
export const wfEditEntrySchema = z.object({
  amount: z.number().int().positive('Enter an amount above zero').max(1_000_000_000),
  reason: z.string().trim().min(3, 'Say why the amount is changing').max(500),
});

// ---------------------------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------------------------

export interface WfPayment {
  id: string;
  paymentNumber: string;
  doctorId: string;
  /** Present on staff-side lists so the queue reads without a second lookup. */
  doctorCode?: string;
  doctorName?: string;
  amount: number;
  method: PaymentMethod;
  bankAccountId: string | null;
  bankAccountLabel?: string | null;
  reference: string | null;
  paidOn: string;
  note: string | null;
  hasProof: boolean;
  proofName: string | null;
  status: WfPaymentStatus;
  submittedByName: string | null;
  submittedSide: 'clinic' | 'lab';
  submittedAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /** The case this payment was collected against, when it was collected case-wise. */
  caseId: string | null;
}

export const wfPaymentSubmitSchema = z.object({
  amount: z.number().int().positive('Enter the amount you paid').max(1_000_000_000),
  method: z.enum(PAYMENT_METHODS),
  bankAccountId: z.string().nullish(),
  reference: txt(120),
  paidOn: z.string().min(1, 'When did you pay?'),
  note: txt(2000),
});
export type WfPaymentSubmit = z.infer<typeof wfPaymentSubmitSchema>;

/** The lab recording a payment it received directly (cash at the counter, etc.). */
export const wfPaymentRecordSchema = wfPaymentSubmitSchema.extend({
  doctorId: z.string().min(1, 'Pick a doctor group'),
  /** Collected against one case (the clinic page's per-case Collect); null = on account. */
  caseId: z.string().min(1).nullish(),
});

export const wfPaymentVerifySchema = z.object({ note: txt(500) });

/**
 * Edit an issued invoice. An invoice is never altered in place (the clinic may already hold it, and
 * its charge and journal are keyed to it): the edit issues a corrected invoice and voids the old one
 * with this reason, in one step. Amounts are paisa; a negative line is a discount.
 */
export const wfInvoiceEditSchema = z
  .object({
    lines: z
      .array(
        z.object({
          description: z.string().trim().min(1, 'Every line needs a description').max(200),
          quantity: z.number().int().min(1, 'Quantity must be at least 1').max(10_000),
          unitAmount: z.number().int().min(-1_000_000_000).max(1_000_000_000),
        }),
      )
      .min(1, 'An invoice needs at least one line')
      .max(50),
    notes: txt(1000),
    reason: z.string().trim().min(3, 'Say why the invoice is being changed').max(500),
  })
  .refine((v) => v.lines.reduce((a, l) => a + l.quantity * l.unitAmount, 0) >= 0, { message: 'The total cannot be negative', path: ['lines'] });
export type WfInvoiceEditRequest = z.infer<typeof wfInvoiceEditSchema>;
export const wfPaymentRejectSchema = z.object({ reason: z.string().trim().min(3, 'Tell the clinic why this was rejected').max(500) });

// ---------------------------------------------------------------------------------------------
// Rupee ↔ paisa at the UI edge
// ---------------------------------------------------------------------------------------------

/** Parse a user-typed rupee amount ("12,500.50") into paisa. Returns null when unparseable. */
export function rupeesToPaisa(input: string | number): number | null {
  const n = typeof input === 'number' ? input : Number(String(input).replace(/[,\s₨]/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** Paisa back to a plain rupee string for prefilling an input (no separators, no symbol). */
export const paisaToRupees = (paisa: number): string => (paisa / 100).toFixed(2);

// ---------------------------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------------------------

export const WF_INVOICE_STATUSES = ['issued', 'void'] as const;
export type WfInvoiceStatus = (typeof WF_INVOICE_STATUSES)[number];

export interface WfInvoiceLine {
  id: string;
  description: string;
  quantity: number;
  /** Paisa per unit. */
  unitAmount: number;
  /** Paisa, quantity x unitAmount. */
  amount: number;
}

/**
 * A clinic invoice for a workflow case.
 *
 * Raised automatically when the aligner count is set — the count is what prices the case — and
 * mirrored onto the doctor's running account as a ledger charge, so the statement and the invoice
 * are two views of one fact rather than two records that can drift.
 *
 * There is no per-invoice settlement: this lab runs an account per doctor group and payments land
 * against the account. "What is still owed" is the account balance, not an invoice status.
 */
/**
 * The lab's own details, as they should appear on a printed document. Carried on the invoice so a
 * printed copy is identical whether the lab or the doctor prints it, and so the portal doesn't need
 * a second endpoint (or permission) just to read the letterhead.
 */
export interface InvoiceLetterhead {
  labName: string;
  labAddress: string | null;
  labPhone: string | null;
  labEmail: string | null;
  labWhatsapp: string | null;
}

export interface WfInvoice {
  id: string;
  invoiceNumber: string;
  doctorId: string;
  caseId: string | null;
  caseNumber: string | null;
  doctorName: string | null;
  clinicName: string | null;
  patientName: string | null;
  issueDate: string;
  currency: string;
  /** Paisa. */
  total: number;
  status: WfInvoiceStatus;
  ledgerEntryId: string | null;
  notes: string | null;
  createdByName: string | null;
  createdAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  lines: WfInvoiceLine[];
  /** Present on a single-invoice read (what the print view uses); omitted from list rows. */
  lab?: InvoiceLetterhead;
}
